/**
 * Sandbox Inspectors — side-effect entry.
 *
 * Auto-mounts the design-mode + comment-mode inspectors inside per-store
 * storefronts running under the AI Builder sandbox dev server. Communicates
 * with the parent /design iframe via postMessage.
 *
 * Always-on: inspectors are passive postMessage listeners and don't affect the
 * page until the AI Builder iframe sends a `*-mode-toggle` message. Gating on
 * NODE_ENV is wrong (always "production" on Vercel) and any build-time gate
 * gets folded out by the commerce-kit bundle, leaving an empty file.
 */

/**
 * Exported so `browser.tsx` can `export { startSandboxInspectors }` — the
 * named binding anchors this module against esbuild tree-shaking when
 * commerce-kit is bundled. Without it, an `export * from "./sandbox-inspectors"`
 * re-exports an empty binding set and esbuild DCEs the whole file.
 */
export function startSandboxInspectors(): void {
	if (typeof window === "undefined") return;
	console.log("[YNS Sandbox Inspectors] module evaluated");
	initDesignModeInspector();
	initCommentModeInspector();
}

// Auto-start on import.
startSandboxInspectors();

interface ElementInfo {
	tag: string;
	id: string | undefined;
	classes: string[];
	textContent: string;
	cssSelector: string;
	boundingRect: { top: number; left: number; width: number; height: number };
	computedStyles: Partial<{
		color: string;
		backgroundColor: string;
		fontSize: string;
		fontFamily: string;
		padding: string;
		margin: string;
	}>;
}

const IGNORED_TAGS = new Set(["HEAD", "SCRIPT", "STYLE", "NOSCRIPT", "HTML"]);

function generateCssSelector(el: Element): string {
	if (el.id) return `${el.tagName.toLowerCase()}#${CSS.escape(el.id)}`;

	const parts: string[] = [];
	let current: Element | null = el;
	while (current && current !== document.body && current !== document.documentElement) {
		let selector = current.tagName.toLowerCase();

		if (current.id) {
			parts.unshift(`${selector}#${CSS.escape(current.id)}`);
			break;
		}

		const classes = Array.from(current.classList).filter((c) => !c.startsWith("data-yns-"));
		if (classes.length > 0) {
			selector += `.${classes.map((c) => CSS.escape(c)).join(".")}`;
		}

		const parent: Element | null = current.parentElement;
		const tag = current.tagName;
		if (parent) {
			const siblings = Array.from(parent.children).filter(
				(s) => s.tagName === tag && (classes.length === 0 || classes.every((c) => s.classList.contains(c))),
			);
			if (siblings.length > 1) {
				const index = Array.from(parent.children).indexOf(current) + 1;
				selector += `:nth-child(${index})`;
			}
		}

		parts.unshift(selector);
		current = current.parentElement;
	}
	return parts.join(" > ");
}

function buildElementInfo(el: Element, textLimit: number): ElementInfo {
	const rect = el.getBoundingClientRect();
	const computed = window.getComputedStyle(el);
	const text = (el.textContent ?? "").trim();
	return {
		tag: el.tagName.toLowerCase(),
		id: el.id || undefined,
		classes: Array.from(el.classList).filter((c) => !c.startsWith("data-yns-")),
		textContent: text.length > textLimit ? `${text.slice(0, textLimit)}…` : text,
		cssSelector: generateCssSelector(el),
		boundingRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
		computedStyles: {
			color: computed.color,
			backgroundColor: computed.backgroundColor,
			fontSize: computed.fontSize,
			fontFamily: computed.fontFamily,
			padding: computed.padding,
			margin: computed.margin,
		},
	};
}

function shouldIgnore(el: Element | null, overlayAttrs: string[]): el is null {
	if (!el?.tagName) return true;
	if (IGNORED_TAGS.has(el.tagName)) return true;
	for (const attr of overlayAttrs) {
		if (el.hasAttribute?.(attr)) return true;
	}
	return false;
}

function initDesignModeInspector() {
	let enabled = false;
	let hoveredElement: Element | null = null;
	let selectedElement: { el: Element; cssSelector: string } | null = null;

	const overlay = document.createElement("div");
	overlay.setAttribute("data-yns-design-overlay", "hover");
	overlay.style.cssText = [
		"position: fixed",
		"pointer-events: none",
		"z-index: 2147483646",
		"border: 2px solid #3b82f6",
		"background: rgba(59, 130, 246, 0.08)",
		"border-radius: 3px",
		"display: none",
		"transition: top 0.05s, left 0.05s, width 0.05s, height 0.05s",
	].join(";");

	const label = document.createElement("div");
	label.setAttribute("data-yns-design-overlay", "label");
	label.style.cssText = [
		"position: fixed",
		"pointer-events: none",
		"z-index: 2147483647",
		"background: #3b82f6",
		"color: #fff",
		"font-size: 11px",
		"font-family: ui-monospace, monospace",
		"padding: 2px 6px",
		"border-radius: 3px",
		"white-space: nowrap",
		"display: none",
	].join(";");

	document.documentElement.appendChild(overlay);
	document.documentElement.appendChild(label);

	const style = document.createElement("style");
	style.setAttribute("data-yns-design-overlay", "style");
	style.textContent = "[data-yns-selected] { outline: 2px solid #3b82f6 !important; outline-offset: 1px; }";
	document.head.appendChild(style);

	function positionOverlay(el: Element) {
		const rect = el.getBoundingClientRect();
		overlay.style.top = `${rect.top}px`;
		overlay.style.left = `${rect.left}px`;
		overlay.style.width = `${rect.width}px`;
		overlay.style.height = `${rect.height}px`;
		overlay.style.display = "block";

		const classes = Array.from(el.classList).filter((c) => !c.startsWith("data-yns-"));
		const labelText = el.tagName.toLowerCase() + (classes.length ? `.${classes[0]}` : "");
		label.textContent = labelText;
		label.style.left = `${rect.left}px`;
		label.style.top = `${Math.max(0, rect.top - 22)}px`;
		label.style.display = "block";
	}

	const handleMouseMove = (e: MouseEvent) => {
		const el = document.elementFromPoint(e.clientX, e.clientY);
		if (shouldIgnore(el, ["data-yns-design-overlay"])) {
			overlay.style.display = "none";
			label.style.display = "none";
			hoveredElement = null;
			return;
		}
		hoveredElement = el;
		requestAnimationFrame(() => {
			if (hoveredElement === el && enabled && el) {
				positionOverlay(el);
			}
		});
	};

	const handleMouseLeave = () => {
		overlay.style.display = "none";
		label.style.display = "none";
		hoveredElement = null;
	};

	const handleClick = (e: MouseEvent) => {
		if (!enabled || !hoveredElement) return;
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		const el = hoveredElement;
		if (shouldIgnore(el, ["data-yns-design-overlay"])) return;

		const info = buildElementInfo(el, 120);
		const selector = info.cssSelector;

		if (selectedElement) {
			selectedElement.el.removeAttribute("data-yns-selected");
		}

		if (selectedElement && selectedElement.cssSelector === selector) {
			selectedElement = null;
			window.parent.postMessage({ type: "element-deselected", data: info }, "*");
		} else {
			selectedElement = { el, cssSelector: selector };
			el.setAttribute("data-yns-selected", "");
			window.parent.postMessage({ type: "element-selected", data: info }, "*");
		}
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Escape" && enabled) {
			disableDesignMode();
			window.parent.postMessage({ type: "design-mode-cleared" }, "*");
		}
	};

	function enableDesignMode() {
		enabled = true;
		document.addEventListener("mousemove", handleMouseMove, true);
		document.addEventListener("mouseleave", handleMouseLeave);
		document.addEventListener("click", handleClick, true);
		document.addEventListener("keydown", handleKeyDown, true);
	}

	function disableDesignMode() {
		enabled = false;
		document.removeEventListener("mousemove", handleMouseMove, true);
		document.removeEventListener("mouseleave", handleMouseLeave);
		document.removeEventListener("click", handleClick, true);
		document.removeEventListener("keydown", handleKeyDown, true);

		overlay.style.display = "none";
		label.style.display = "none";
		hoveredElement = null;

		if (selectedElement) {
			selectedElement.el.removeAttribute("data-yns-selected");
			selectedElement = null;
		}
	}

	window.addEventListener("message", (event) => {
		const msg = event.data;
		if (!msg || typeof msg !== "object") return;

		if (msg.type === "design-mode-toggle") {
			if (msg.enabled) {
				enableDesignMode();
			} else {
				disableDesignMode();
				window.parent.postMessage({ type: "design-mode-cleared" }, "*");
			}
		}

		if (msg.type === "design-mode-deselect") {
			if (selectedElement) {
				selectedElement.el.removeAttribute("data-yns-selected");
				selectedElement = null;
			}
		}
	});
}

function initCommentModeInspector() {
	let enabled = false;
	let hoveredElement: Element | null = null;
	let pins: Array<{ id: string; selector: string; number: number }> = [];

	const overlay = document.createElement("div");
	overlay.setAttribute("data-yns-comment-overlay", "hover");
	overlay.style.cssText = [
		"position: fixed",
		"pointer-events: none",
		"z-index: 2147483644",
		"border: 2px dashed #10b981",
		"background: rgba(16, 185, 129, 0.06)",
		"border-radius: 3px",
		"display: none",
		"transition: top 0.05s, left 0.05s, width 0.05s, height 0.05s",
	].join(";");

	const cursorLabel = document.createElement("div");
	cursorLabel.setAttribute("data-yns-comment-overlay", "cursor-label");
	cursorLabel.textContent = "Click to comment";
	cursorLabel.style.cssText = [
		"position: fixed",
		"pointer-events: none",
		"z-index: 2147483645",
		"background: #059669",
		"color: #fff",
		"font-size: 11px",
		"font-family: ui-sans-serif, system-ui, sans-serif",
		"padding: 3px 8px",
		"border-radius: 4px",
		"white-space: nowrap",
		"display: none",
		"box-shadow: 0 2px 6px rgba(0,0,0,0.15)",
	].join(";");

	const pinContainer = document.createElement("div");
	pinContainer.setAttribute("data-yns-comment-overlay", "pins");
	pinContainer.style.cssText =
		"position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 2147483643;";

	document.documentElement.appendChild(overlay);
	document.documentElement.appendChild(cursorLabel);
	document.documentElement.appendChild(pinContainer);

	function renderPins() {
		pinContainer.innerHTML = "";
		for (const pin of pins) {
			let el: Element | null = null;
			try {
				el = document.querySelector(pin.selector);
			} catch {
				el = null;
			}
			if (!el) continue;

			const r = el.getBoundingClientRect();
			const pinEl = document.createElement("div");
			pinEl.style.cssText = [
				"position: fixed",
				`top: ${r.top - 12}px`,
				`left: ${r.left + r.width / 2 - 12}px`,
				"width: 24px",
				"height: 24px",
				"border-radius: 50%",
				"background: #059669",
				"color: #fff",
				"display: flex",
				"align-items: center",
				"justify-content: center",
				"font-size: 12px",
				"font-weight: 600",
				"font-family: ui-sans-serif, system-ui, sans-serif",
				"box-shadow: 0 2px 8px rgba(0,0,0,0.2)",
				"pointer-events: none",
			].join(";");
			pinEl.textContent = String(pin.number);
			pinContainer.appendChild(pinEl);
		}
	}

	const handleMouseMove = (e: MouseEvent) => {
		const el = document.elementFromPoint(e.clientX, e.clientY);
		if (shouldIgnore(el, ["data-yns-comment-overlay", "data-yns-design-overlay"])) {
			overlay.style.display = "none";
			cursorLabel.style.display = "none";
			hoveredElement = null;
			return;
		}
		hoveredElement = el;
		requestAnimationFrame(() => {
			if (hoveredElement === el && enabled && el) {
				const rect = el.getBoundingClientRect();
				overlay.style.top = `${rect.top}px`;
				overlay.style.left = `${rect.left}px`;
				overlay.style.width = `${rect.width}px`;
				overlay.style.height = `${rect.height}px`;
				overlay.style.display = "block";

				cursorLabel.style.left = `${e.clientX + 14}px`;
				cursorLabel.style.top = `${e.clientY + 14}px`;
				cursorLabel.style.display = "block";
			}
		});
	};

	const handleMouseLeave = () => {
		overlay.style.display = "none";
		cursorLabel.style.display = "none";
		hoveredElement = null;
	};

	const handleClick = (e: MouseEvent) => {
		if (!enabled || !hoveredElement) return;
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		const el = hoveredElement;
		if (shouldIgnore(el, ["data-yns-comment-overlay", "data-yns-design-overlay"])) return;

		const info = buildElementInfo(el, 200);
		const rect = el.getBoundingClientRect();

		window.parent.postMessage(
			{
				type: "comment-click",
				data: {
					element: info,
					clickPosition: { x: e.clientX, y: e.clientY },
					elementRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
					pagePath: window.location.pathname,
				},
			},
			"*",
		);
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Escape" && enabled) {
			disableCommentMode();
			window.parent.postMessage({ type: "comment-mode-cleared" }, "*");
		}
	};

	function enableCommentMode() {
		enabled = true;
		document.body.style.cursor = "crosshair";
		document.addEventListener("mousemove", handleMouseMove, true);
		document.addEventListener("mouseleave", handleMouseLeave);
		document.addEventListener("click", handleClick, true);
		document.addEventListener("keydown", handleKeyDown, true);
	}

	function disableCommentMode() {
		enabled = false;
		document.body.style.cursor = "";
		document.removeEventListener("mousemove", handleMouseMove, true);
		document.removeEventListener("mouseleave", handleMouseLeave);
		document.removeEventListener("click", handleClick, true);
		document.removeEventListener("keydown", handleKeyDown, true);

		overlay.style.display = "none";
		cursorLabel.style.display = "none";
		hoveredElement = null;
	}

	let rafPending = false;
	const scheduleRender = () => {
		if (rafPending) return;
		rafPending = true;
		requestAnimationFrame(() => {
			rafPending = false;
			renderPins();
		});
	};
	window.addEventListener("scroll", scheduleRender, { passive: true });
	window.addEventListener("resize", scheduleRender, { passive: true });

	window.addEventListener("message", (event) => {
		const msg = event.data;
		if (!msg || typeof msg !== "object") return;

		if (msg.type === "comment-mode-toggle") {
			if (msg.enabled) enableCommentMode();
			else disableCommentMode();
		}

		if (msg.type === "comment-pins-update") {
			pins = msg.pins ?? [];
			renderPins();
		}

		if (msg.type === "comment-pin-remove") {
			pins = pins.filter((p) => p.id !== msg.pinId);
			renderPins();
		}
	});
}
